import axios from "api/axios"
import { APIResponse, ConfigWithURLParams } from "api/types"
import { formatErrorResponse, formatSuccessResponse } from "api/utils"

import {
  AiDetectedSubmissionReport,
  AttachmentAIDetection,
  PlagiarismSource,
  PreviousSubmissionListItem,
  Submission,
  SubmissionPlagiarismReport,
} from "./types"
import { urls } from "./urls"

export default {
  retrieve: async ({
    params,
    urlParams,
  }: ConfigWithURLParams<"submissionId">): Promise<APIResponse<Submission>> => {
    try {
      const res = await axios.get(
        urls.submissions.retrieve(urlParams.submissionId),
        {
          params,
        }
      )
      return formatSuccessResponse(res)
    } catch (e) {
      throw formatErrorResponse(e)
    }
  },
  listAssignmentUserSubmissions: async ({
    params,
    urlParams,
  }: ConfigWithURLParams<"assignmentId" | "userId">): Promise<
    APIResponse<PreviousSubmissionListItem[]>
  > => {
    try {
      const res = await axios.get(
        urls.submissions.listAssignmentUserSubmissions(
          urlParams.assignmentId,
          urlParams.userId
        ),
        {
          params,
        }
      )
      return formatSuccessResponse(res)
    } catch (e) {
      throw formatErrorResponse(e)
    }
  },
  getPlagiarismSources: async ({
    params,
    urlParams,
  }: ConfigWithURLParams<"submissionId" | "sourceId">): Promise<
    APIResponse<PlagiarismSource>
  > => {
    try {
      const res = await axios.get(
        urls.plagiarism.getPlagiarismSources(
          urlParams.submissionId,
          urlParams.sourceId
        ),
        { params }
      )
      return formatSuccessResponse(res)
    } catch (e) {
      throw formatErrorResponse(e)
    }
  },
  getPlagiarismReport: async ({
    urlParams,
  }: ConfigWithURLParams<"submissionId">): Promise<
    APIResponse<SubmissionPlagiarismReport>
  > => {
    try {
      const res = await axios.get(
        urls.plagiarism.getPlagiarisedSubmission(urlParams.submissionId)
      )

      return formatSuccessResponse(res)
    } catch (e) {
      throw formatErrorResponse(e)
    }
  },
  listAssessors: async (): Promise<
    APIResponse<{ uuid: string; fullName: string }[]>
  > => {
    try {
      const res = await axios.get(urls.users.assessors())

      return formatSuccessResponse(res)
    } catch (e) {
      throw formatErrorResponse(e)
    }
  },
  getAiDetectedSubmissionReport: async ({
    urlParams,
  }: ConfigWithURLParams<"submissionId">): Promise<
    APIResponse<AiDetectedSubmissionReport>
  > => {
    try {
      const res = await axios.get(
        urls.aiContent.getAiDetectedSubmission(urlParams.submissionId)
      )

      return formatSuccessResponse(res)
    } catch (e) {
      throw formatErrorResponse(e)
    }
  },
  rerunAiDetection: async ({
    urlParams,
  }: ConfigWithURLParams<"submissionId">): Promise<
    APIResponse<{ submissionId: string }>
  > => {
    try {
      const res = await axios.post(
        urls.aiContent.rerun(urlParams.submissionId),
        {}
      )
      return formatSuccessResponse(res)
    } catch (e) {
      throw formatErrorResponse(e)
    }
  },
  rerunPlagiarism: async ({
    urlParams,
  }: ConfigWithURLParams<"submissionId">): Promise<
    APIResponse<{ submissionId: string }>
  > => {
    try {
      const res = await axios.post(
        urls.plagiarism.rerun(urlParams.submissionId),
        {}
      )
      return formatSuccessResponse(res)
    } catch (e) {
      throw formatErrorResponse(e)
    }
  },
  uploadGrammarlyReport: async ({
    urlParams,
    data,
  }: ConfigWithURLParams<"submissionId">): Promise<
    APIResponse<{ grammarlyReport: string }>
  > => {
    try {
      const res = await axios.put(
        urls.plagiarism.grammarlyReport(urlParams.submissionId),
        data
      )
      return formatSuccessResponse(res)
    } catch (e) {
      throw formatErrorResponse(e)
    }
  },
  listAttachments: async ({
    urlParams,
  }: ConfigWithURLParams<"submissionId">): Promise<
    APIResponse<AttachmentAIDetection[]>
  > => {
    try {
      const res = await axios.get(
        urls.aiContent.attachments(urlParams.submissionId)
      )
      return formatSuccessResponse(res)
    } catch (e) {
      throw formatErrorResponse(e)
    }
  },
  registerAttachments: async ({
    urlParams,
    data,
  }: ConfigWithURLParams<"submissionId">): Promise<
    APIResponse<AttachmentAIDetection[]>
  > => {
    try {
      const res = await axios.post(
        urls.aiContent.attachments(urlParams.submissionId),
        data
      )
      return formatSuccessResponse(res)
    } catch (e) {
      throw formatErrorResponse(e)
    }
  },
}
